import React, { useState } from 'react';
import styled from '@emotion/styled';
import Button from '../../../components/general/Button';
import WarningConfirmation from '../../../components/general/WarningConfirmation';
import ConfirmationDialog from '../../../components/structure/ConfirmationDialog';
import SettingsCard from '../../../components/structure/SettingsCard';
import OrganizationContainer from '../../../state/containers/OrganizationContainer';
import { color_shades_darker } from '../../../constants/colors';

interface OrganizationStatusSettingsProps {
    orgContainer: OrganizationContainer
}

const OrganizationStatusSettings: React.FC<OrganizationStatusSettingsProps> = ({ orgContainer }) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [error, setError] = useState('');
    const isDisabled = orgContainer.state.currentOrganization?.isDisabled === true;
    const requestedDisabledState = !isDisabled;

    const handleClose = () => {
        if (orgContainer.state.isUpdatingDisabledState) return;
        setError('');
        setShowConfirmation(false);
    };

    const handleConfirm = async () => {
        setError('');
        const result = await orgContainer.updateCurrentOrganizationDisabled(requestedDisabledState);
        if (result.resultType === 'Ok') {
            setShowConfirmation(false);
        } else {
            setError(result.errors.join(' '));
        }
    };

    const action = isDisabled ? 'Enable' : 'Disable';
    const confirmationText = isDisabled
        ? 'Enabling this organization will restore it to organization selection and revenue views. Its stored data and billing configuration have been preserved.'
        : 'Disabling this organization hides it from organization selection and revenue views. Nothing is deleted, and you can re-enable the organization at any time to restore access.';

    return (
        <SettingsCard
            title="Organization Status"
            subtitle="Control whether this organization appears in organization selection and revenue views."
        >
            <StatusBody>
                <StatusText>
                    This organization is currently <strong>{isDisabled ? 'disabled' : 'enabled'}</strong>.
                </StatusText>
                <Button
                    themes={[isDisabled ? 'primary' : 'destructive']}
                    text={`${action} Organization`}
                    type="button"
                    disabled={orgContainer.state.isUpdatingDisabledState}
                    onClick={() => setShowConfirmation(true)}
                />
            </StatusBody>
            {showConfirmation && (
                <ConfirmationDialog
                    title={`${action} Organization`}
                    deleteText={`Yes, ${action}`}
                    warning={isDisabled}
                    isLoading={orgContainer.state.isUpdatingDisabledState}
                    onClose={handleClose}
                    onConfirm={handleConfirm}
                >
                    <WarningConfirmation
                        text={confirmationText}
                        useWarningColor={isDisabled}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </ConfirmationDialog>
            )}
        </SettingsCard>
    );
};

const StatusBody = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
`;

const StatusText = styled.p`
    color: ${color_shades_darker};
    font-family: Muli;
    font-size: 14px;
    margin: 0 24px 0 0;
`;

const ErrorMessage = styled.p`
    color: #bd251a;
    font-family: Muli;
    font-size: 14px;
    margin: -48px 32px 24px;
    text-align: center;
`;

export default OrganizationStatusSettings;
